import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  ClearDefaultEntityViewDocument,
  CreateEntityViewDocument,
  DeleteEntityViewDocument,
  EntityViewsByTypeDocument,
  EntityViewsDocument,
  SetDefaultEntityViewDocument,
  UpdateEntityViewDocument,
  unwrapResult,
  resultErrorMessage,
  type CreateEntityViewInput,
  type EntityViewRow,
  type EntityViewScopeKind,
  type UpdateEntityViewInput,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * THE saved-view store for one entity scope — a workflow's tab row, or the
 * workspace's own dashboards.
 *
 * A workflow's views are WORKSPACE-SHARED: the rows this returns are the tabs
 * every member of the workspace sees, and every write is gated on
 * `create.views` server side. A workspace dashboard carries its own ACL
 * instead — the list holds only the rows the viewer can reach, and writes
 * follow the row's `viewerAccessLevel`. Either way a refused write comes back
 * `null`/`false`; the panel disables the affordances rather than firing a
 * call it knows will be refused.
 *
 * The scope's built-in views (List / Board / Dashboard) are seeded by the
 * server on first read, so a workflow never comes back with an empty tab row
 * and there is no client-side "create the defaults" path to keep in sync.
 */

const EMPTY_VIEWS: EntityViewRow[] = [];

export type EntityViewsScope = {
  kind: EntityViewScopeKind;
  id: string;
};

type UseEntityViewsResult = {
  views: EntityViewRow[];
  /** True only before the first response — never gate visibility on a
   *  refetch (the tab row must not blink when a view is renamed). */
  initialLoading: boolean;
  /** The query error when the list has NOTHING to show — a failed first load,
   *  or a refetch that failed with an empty cache. Consumers gate an error
   *  state on this, never on `views.length === 0` alone: an empty answer is
   *  also what an unseeded scope returns for one round trip. */
  loadError: Error | null;
  /** Re-issues the list query — the retry behind the load-error state. */
  retry: () => void;
  createView: (input: CreateEntityViewInput) => Promise<EntityViewRow | null>;
  updateView: (
    id: string,
    input: UpdateEntityViewInput,
  ) => Promise<EntityViewRow | null>;
  deleteView: (id: string) => Promise<boolean>;
  /** Turns the scope's default ON for this view (the previous default goes
   *  off with it) or OFF (the scope then has none, and opens on its first
   *  view). */
  setDefaultView: (id: string, isDefault: boolean) => Promise<boolean>;
};

/**
 * Create a saved view in ANY scope — the one write that has a caller outside
 * a scope's own tab row (the Dashboards module's Create Dashboard, which makes
 * a workspace-scoped row). `useEntityViews().createView` is this, pinned to
 * its scope.
 *
 * The new row is not in any connection — both reads are plain list fields —
 * so it is appended to each cached query rather than left to normalization
 * (which only refreshes rows already listed): the scope's tab row, and the
 * workspace-wide by-type list the dashboards directory renders. A scope list
 * that is not in the cache yet is SEEDED with the row: the caller lands on
 * the new view at once, and the workspace scope's list has never been read
 * when its first dashboard is made — without the seed the page spins for a
 * round trip and then paints. The seed is provisional (`cache-and-network`
 * replaces it with the server's list on the page's first read); the by-type
 * list is left alone when absent, since its first read includes the row.
 */
export function useCreateEntityView(): (
  scope: EntityViewsScope,
  input: CreateEntityViewInput,
) => Promise<EntityViewRow | null> {
  const [createMutation] = useMutation(CreateEntityViewDocument);

  return useCallback(
    async (scope, input) => {
      const scopeVariables = { scopeKind: scope.kind, scopeId: scope.id };
      try {
        const { data: result } = await createMutation({
          variables: { ...scopeVariables, input },
          update: (cache, { data: payload }) => {
            const created = payload?.createEntityView;
            if (created?.__typename !== "EntityView") return;
            cache.updateQuery(
              { query: EntityViewsDocument, variables: scopeVariables },
              (prev) =>
                prev
                  ? { ...prev, entityViews: [...prev.entityViews, created] }
                  : { entityViews: [created] },
            );
            cache.updateQuery(
              {
                query: EntityViewsByTypeDocument,
                variables: { type: input.type },
              },
              (prev) =>
                prev
                  ? {
                      ...prev,
                      entityViewsByType: [...prev.entityViewsByType, created],
                    }
                  : prev,
            );
          },
        });
        const unwrapped = unwrapResult(result?.createEntityView);
        if (!unwrapped.ok) {
          toastManager.add({
            title: resultErrorMessage(unwrapped.error, "Failed to add view"),
            type: "error",
          });
          return null;
        }
        return unwrapped.data;
      } catch (err) {
        toastMutationError(err, "Failed to add view");
        return null;
      }
    },
    [createMutation],
  );
}

/**
 * Update a saved view's stored fields (name, icon, config…) — scope-free,
 * since the row normalizes off the response and no list changes shape. The
 * Dashboards directory's Rename writes through this; `useEntityViews()
 * .updateView` is the same call.
 */
export function useUpdateEntityView(): (
  id: string,
  input: UpdateEntityViewInput,
) => Promise<EntityViewRow | null> {
  const [updateMutation] = useMutation(UpdateEntityViewDocument);

  return useCallback(
    async (id, input) => {
      try {
        const { data: result } = await updateMutation({
          variables: { id, input },
        });
        return result?.updateEntityView ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to update view");
        return null;
      }
    },
    [updateMutation],
  );
}

/**
 * Delete a saved view in ANY scope — the other write with a caller outside a
 * scope's own tab row (the Dashboards directory's row menu, which acts on a
 * dashboard in whichever workflow or workspace scope hosts it).
 * `useEntityViews().deleteView` is this, pinned to its scope.
 *
 * The scope's cached tab row drops the id; the row itself is evicted, and
 * the workspace-wide by-type list (a plain list of references) sheds the
 * dangling reference on its next read — so the directory row goes with it.
 */
export function useDeleteEntityView(): (
  scope: EntityViewsScope,
  id: string,
) => Promise<boolean> {
  const [deleteMutation] = useMutation(DeleteEntityViewDocument);

  return useCallback(
    async (scope, id) => {
      const scopeVariables = { scopeKind: scope.kind, scopeId: scope.id };
      try {
        const { data: result } = await deleteMutation({
          variables: { id },
          update: (cache, { data: payload }) => {
            if (payload?.deleteEntityView?.__typename !== "EntityView") return;
            cache.updateQuery(
              { query: EntityViewsDocument, variables: scopeVariables },
              (prev) =>
                prev
                  ? {
                      ...prev,
                      entityViews: prev.entityViews.filter(
                        (view) => view.id !== id,
                      ),
                    }
                  : prev,
            );
            const cacheId = cache.identify({ __typename: "EntityView", id });
            // An absent id reads as ROOT, which would evict a root field.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        const unwrapped = unwrapResult(result?.deleteEntityView);
        if (!unwrapped.ok) {
          toastManager.add({
            title: resultErrorMessage(unwrapped.error, "Failed to delete view"),
            type: "error",
          });
          return false;
        }
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to delete view");
        return false;
      }
    },
    [deleteMutation],
  );
}

export function useEntityViews(
  scope: EntityViewsScope | null,
): UseEntityViewsResult {
  const variables = useMemo(
    () =>
      scope ? { scopeKind: scope.kind, scopeId: scope.id } : undefined,
    [scope],
  );

  const { data, loading, error, refetch } = useQuery(EntityViewsDocument, {
    variables: variables ?? { scopeKind: "workflow", scopeId: "" },
    skip: !variables,
  });

  const [setDefaultMutation] = useMutation(SetDefaultEntityViewDocument);
  const [clearDefaultMutation] = useMutation(ClearDefaultEntityViewDocument);
  const createEntityView = useCreateEntityView();
  const deleteEntityView = useDeleteEntityView();

  const views = data?.entityViews ?? EMPTY_VIEWS;

  const createView = useCallback(
    async (input: CreateEntityViewInput) => {
      if (!scope) return null;
      return createEntityView(scope, input);
    },
    [createEntityView, scope],
  );

  const updateView = useUpdateEntityView();

  const deleteView = useCallback(
    async (id: string) => {
      if (!scope) return false;
      return deleteEntityView(scope, id);
    },
    [deleteEntityView, scope],
  );

  // Both mutations answer with the whole scope: setting a default clears the
  // previous one, clearing leaves none, and every row normalizes off the
  // response. The mark moves or goes while the eye is still on the menu that
  // did it, so the change is also said out loud.
  const setDefaultView = useCallback(
    async (id: string, isDefault: boolean) => {
      try {
        let rows: EntityViewRow[];
        if (isDefault) {
          const { data: result } = await setDefaultMutation({
            variables: { id },
          });
          rows = result?.setDefaultEntityView ?? [];
        } else {
          const { data: result } = await clearDefaultMutation({
            variables: { id },
          });
          rows = result?.clearDefaultEntityView ?? [];
        }
        return rows.length > 0;
      } catch (err) {
        toastMutationError(
          err,
          isDefault
            ? "Failed to set the default view"
            : "Failed to turn off the default view",
        );
        return false;
      }
    },
    [clearDefaultMutation, setDefaultMutation],
  );

  const retry = useCallback(() => {
    void refetch().catch(() => {
      // The failure re-lands in `error`; the state that called this renders it.
    });
  }, [refetch]);

  return {
    views,
    initialLoading: loading && !data,
    loadError: data ? null : (error ?? null),
    retry,
    createView,
    updateView,
    deleteView,
    setDefaultView,
  };
}
